import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { HelpCircle, X, Send, Plus, ChevronLeft, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityColors = {
  low: 'text-slate-400 bg-slate-500/20',
  normal: 'text-blue-400 bg-blue-500/20',
  high: 'text-red-400 bg-red-500/20'
};

const statusColors = {
  open: 'text-yellow-400 bg-yellow-500/20',
  in_progress: 'text-blue-400 bg-blue-500/20',
  resolved: 'text-green-400 bg-green-500/20',
  closed: 'text-slate-400 bg-slate-500/20'
};

const statusIcons = {
  open: Clock,
  in_progress: Loader2,
  resolved: CheckCircle,
  closed: X
};

export default function SupportWidget() {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list', 'new', 'detail'
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // New ticket form
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  
  // Reply form
  const [replyMessage, setReplyMessage] = useState('');
  
  const messagesEndRef = useRef(null);

  const fetchTickets = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data.ticket);
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      fetchTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  useEffect(() => {
    if (selectedTicket?.messages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  const createTicket = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newMessage.trim()) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/support/tickets`,
        { subject: newSubject, message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Support ticket created');
      setNewSubject('');
      setNewMessage('');
      setView('list');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/support/tickets/${selectedTicket.ticket_id}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplyMessage('');
      fetchTicketDetail(selectedTicket.ticket_id);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setLoading(false);
    }
  };

  const closeTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      await axios.post(
        `${API_URL}/api/support/tickets/${selectedTicket.ticket_id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket closed');
      setView('list');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
    fetchTicketDetail(ticket.ticket_id);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!user) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => toast.info('Please login to access support')}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-110 transition-transform"
        >
          <HelpCircle size={24} className="text-white" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Support Panel */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 sm:w-96 bg-[#0a0e14] border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              {view !== 'list' && (
                <button
                  onClick={() => { setView('list'); setSelectedTicket(null); }}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} className="text-slate-400" />
                </button>
              )}
              <h3 className="text-white font-semibold flex-1 text-center">
                {view === 'list' ? 'Support' : view === 'new' ? 'New Ticket' : 'Ticket Details'}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="h-96 overflow-y-auto">
            {view === 'list' && (
              <div className="p-4 space-y-3">
                {/* New Ticket Button */}
                <button
                  onClick={() => setView('new')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-xl text-blue-400 font-medium transition-colors"
                >
                  <Plus size={18} />
                  Create New Ticket
                </button>

                {/* Tickets List */}
                {tickets.length === 0 ? (
                  <div className="text-center py-8">
                    <HelpCircle size={40} className="mx-auto text-slate-600 mb-3" />
                    <p className="text-slate-500 text-sm">No support tickets yet</p>
                    <p className="text-slate-600 text-xs mt-1">Create one if you need help!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tickets.map((ticket) => {
                      const StatusIcon = statusIcons[ticket.status] || Clock;
                      return (
                        <button
                          key={ticket.ticket_id}
                          onClick={() => openTicket(ticket)}
                          className="w-full text-left p-3 bg-slate-800/50 hover:bg-slate-800 rounded-xl transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
                              <p className="text-slate-500 text-xs mt-1 truncate">
                                {ticket.messages?.[ticket.messages.length - 1]?.message}
                              </p>
                            </div>
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[ticket.status]}`}>
                              <StatusIcon size={12} className={ticket.status === 'in_progress' ? 'animate-spin' : ''} />
                              {ticket.status.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-xs mt-2">{formatTime(ticket.updated_at)}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {view === 'new' && (
              <form onSubmit={createTicket} className="p-4 space-y-4">
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Subject</label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder="Brief description of your issue"
                    maxLength={100}
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Message</label>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Describe your issue in detail..."
                    maxLength={1000}
                    rows={5}
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 resize-none"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading || !newSubject.trim() || !newMessage.trim()}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Ticket'}
                </button>
              </form>
            )}

            {view === 'detail' && selectedTicket && (
              <div className="flex flex-col h-full">
                {/* Ticket Header */}
                <div className="p-4 border-b border-slate-700/50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{selectedTicket.subject}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${priorityColors[selectedTicket.priority]}`}>
                          {selectedTicket.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[selectedTicket.status]}`}>
                          {selectedTicket.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    {selectedTicket.status !== 'closed' && (
                      <button
                        onClick={closeTicket}
                        className="text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        Close Ticket
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {selectedTicket.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                          msg.sender === 'admin'
                            ? 'bg-blue-600/20 text-blue-100 rounded-bl-sm'
                            : 'bg-slate-700/50 text-slate-200 rounded-br-sm'
                        }`}
                      >
                        <p className="text-xs font-medium mb-1 opacity-70">
                          {msg.sender === 'admin' ? '🛡️ Support' : msg.username}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-[10px] opacity-50 mt-1">{formatTime(msg.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                {selectedTicket.status !== 'closed' && (
                  <form onSubmit={sendReply} className="p-3 border-t border-slate-700/50">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type a reply..."
                        maxLength={1000}
                        className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                      />
                      <button
                        type="submit"
                        disabled={loading || !replyMessage.trim()}
                        className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-xl transition-colors"
                      >
                        <Send size={18} className="text-white" />
                      </button>
                    </div>
                  </form>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="p-3 border-t border-slate-700/50 bg-slate-800/30">
                    <p className="text-center text-slate-500 text-sm">This ticket has been closed</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'bg-slate-700 rotate-90'
            : 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-blue-500/30 hover:scale-110'
        }`}
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <HelpCircle size={24} className="text-white" />
        )}
        {/* Notification badge for unread */}
        {!isOpen && tickets.some(t => t.status === 'in_progress') && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
            !
          </span>
        )}
      </button>
    </div>
  );
}
